/**
 * =============================================================================
 * Componente: AffiliateBottomNav
 * Versão: 1.0.0 (Redesign Premium)
 *
 * Descrição:
 *   Barra de navegação inferior fixa, padrão "app moderno", exibida apenas
 *   no mobile (<768px). 5 itens: Início, Campanhas, Indicações, Comissões
 *   e "Mais" (abre o sheet com itens secundários).
 * =============================================================================
 */
import { NavLink } from 'react-router-dom';
import { Home, Megaphone, Users, Wallet, MoreHorizontal, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AffiliateBottomNavProps {
  /** Abre o sheet "Mais" (saques, materiais, perfil, sair). */
  onOpenMore: () => void;
}

interface BottomNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

const ITEMS: BottomNavItem[] = [
  { to: '/afiliado', label: 'Início', icon: Home, end: true },
  { to: '/afiliado/links', label: 'Campanhas', icon: Megaphone },
  { to: '/afiliado/indicacoes', label: 'Indicações', icon: Users },
  { to: '/afiliado/comissoes', label: 'Comissões', icon: Wallet },
];

export function AffiliateBottomNav({ onOpenMore }: AffiliateBottomNavProps) {
  return (
    <nav
      aria-label="Navegação principal"
      className={cn(
        'fixed bottom-0 left-0 right-0 z-40 md:hidden',
        'bg-[hsl(var(--aff-surface))]/95 backdrop-blur-xl',
        'border-t border-[hsl(var(--aff-border))]',
        'aff-safe-bottom',
      )}
    >
      <ul className="grid grid-cols-5 px-1">
        {ITEMS.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10.5px] font-medium transition-colors',
                  isActive
                    ? 'text-[hsl(var(--aff-primary))]'
                    : 'text-[hsl(var(--aff-muted-foreground))]',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={cn(
                      'relative flex h-9 w-9 items-center justify-center rounded-full transition-all',
                      isActive && 'bg-[hsl(var(--aff-primary-soft))]',
                    )}
                  >
                    <item.icon
                      className="h-[18px] w-[18px]"
                      strokeWidth={isActive ? 2.4 : 2}
                    />
                  </span>
                  <span className="leading-none">{item.label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
        <li>
          <button
            type="button"
            onClick={onOpenMore}
            className={cn(
              'flex w-full flex-col items-center justify-center gap-0.5 py-2.5 text-[10.5px] font-medium',
              'text-[hsl(var(--aff-muted-foreground))]',
            )}
            aria-label="Mais opções"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full">
              <MoreHorizontal className="h-[18px] w-[18px]" strokeWidth={2} />
            </span>
            <span className="leading-none">Mais</span>
          </button>
        </li>
      </ul>
    </nav>
  );
}
